/**
 * SDK custom events — operator UI (feature FS-206).
 *
 * Each tenant keeps its own registry of SDK event type names. There is no
 * hard cap — operators may register as many event types as they need
 * (`level_up`, `tutorial_complete`, `purchase`, `share`, `rate_app`, ...).
 *
 * Composition:
 *   - Header with emerald→sky gradient headline.
 *   - Stats panel showing total events, per-category breakdown, top 10 by
 *     volume.
 *   - Events registry card: search box, category filter chips, add-button
 *     that opens a modal form to register a new event type.
 *   - Registry table: edit, delete.
 *
 * Brand: emerald→sky gradient headline, zinc borders, 6-8px rounded, light
 * theme, no emojis, no competitor names.
 */
import {
	useCallback,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	categoryLabel,
	createEventType,
	deleteEventType,
	EVENT_CATEGORIES,
	fetchStats,
	formatCents,
	formatDate,
	formatVolume,
	listEventTypes,
	sdkCustomEventsQueryKeys,
	updateEventType,
	validateCategory,
	validateDisplayName,
	validateName,
	validatePayoutCents,
	type EventCategory,
	type SdkEventType,
	type StatsResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function SdkCustomEventsPage() {
	const queryClient = useQueryClient();

	const [categoryFilter, setCategoryFilter] = useState<EventCategory | null>(
		null,
	);
	const [search, setSearch] = useState("");
	const [modalOpen, setModalOpen] = useState(false);

	const listQuery = useQuery({
		queryKey: sdkCustomEventsQueryKeys.list(categoryFilter),
		queryFn: () => listEventTypes(categoryFilter ?? undefined),
	});

	const statsQuery = useQuery({
		queryKey: sdkCustomEventsQueryKeys.stats(),
		queryFn: fetchStats,
	});

	const invalidateAll = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: sdkCustomEventsQueryKeys.all,
		});
	}, [queryClient]);

	const createMutation = useMutation({
		mutationFn: createEventType,
		onSuccess: (row) => {
			toast.success(`Event "${row.name}" registered.`);
			invalidateAll();
			setModalOpen(false);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register event.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateEventType,
		onSuccess: (row) => {
			toast.success(`Event "${row.name}" updated.`);
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update event.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteEventType,
		onSuccess: () => {
			toast.success("Event soft-deleted.");
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to delete event.");
		},
	});

	const items: readonly SdkEventType[] = listQuery.data?.items ?? [];

	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return items;
		return items.filter(
			(r) =>
				r.name.toLowerCase().includes(needle) ||
				r.display_name.toLowerCase().includes(needle),
		);
	}, [items, search]);

	return (
		<main
			data-testid="sdk-custom-events-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header onRegister={() => setModalOpen(true)} />
			<StatsPanel
				stats={statsQuery.data ?? null}
				loading={statsQuery.isLoading}
				error={statsQuery.error?.message ?? null}
			/>
			<RegistryCard
				rows={filtered}
				totalRows={items.length}
				loading={listQuery.isLoading}
				error={listQuery.error?.message ?? null}
				categoryFilter={categoryFilter}
				onCategoryFilter={setCategoryFilter}
				search={search}
				onSearch={setSearch}
				onEdit={(args) => updateMutation.mutate(args)}
				onDelete={(id) => deleteMutation.mutate(id)}
				onAdd={() => setModalOpen(true)}
				updating={updateMutation.isPending}
				deleting={deleteMutation.isPending}
			/>
			{modalOpen ? (
				<RegisterModal
					onClose={() => setModalOpen(false)}
					onSubmit={(args) => createMutation.mutate(args)}
					busy={createMutation.isPending}
				/>
			) : null}
		</main>
	);
}

export default SdkCustomEventsPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header(props: { onRegister: () => void }) {
	return (
		<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
			<div className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					SDK
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
					Custom events
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Register as many SDK event types as you like —{" "}
					<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
						level_up
					</code>
					,{" "}
					<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
						tutorial_complete
					</code>
					,{" "}
					<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
						share
					</code>
					, or anything your SDK emits. No hard cap per tenant.
				</p>
			</div>
			<button
				type="button"
				className={BTN_PRIMARY}
				onClick={props.onRegister}
				data-testid="open-register-modal"
			>
				Add event type
			</button>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Stats panel
// ---------------------------------------------------------------------------

function StatsPanel(props: {
	stats: StatsResponse | null;
	loading: boolean;
	error: string | null;
}) {
	return (
		<section className={`${CARD} p-5`} aria-label="Event stats">
			<header className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">Overview</h2>
				<p className={HINT}>
					Aggregate counts by category plus the top 10 by lifetime volume.
				</p>
			</header>
			{props.error ? (
				<p className="mt-4 text-sm text-red-600" role="alert">
					{props.error}
				</p>
			) : props.loading ? (
				<p className="mt-4 text-sm text-zinc-500">Loading stats.</p>
			) : !props.stats || props.stats.total_events === 0 ? (
				<p className="mt-4 text-sm text-zinc-500">
					No event types registered yet. Click "Add event type" to start.
				</p>
			) : (
				<>
					<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-7">
						<StatTile
							label="Total events"
							value={formatVolume(props.stats.total_events)}
						/>
						{EVENT_CATEGORIES.map((cat) => (
							<StatTile
								key={cat}
								label={categoryLabel(cat)}
								value={formatVolume(props.stats!.by_category[cat] ?? 0)}
							/>
						))}
					</div>
					{props.stats.top_10_by_volume.length > 0 ? (
						<div className="mt-5 overflow-x-auto">
							<h3 className="mb-2 text-sm font-semibold text-zinc-700">
								Top 10 by volume
							</h3>
							<table
								className="w-full border-collapse text-left text-sm"
								data-testid="top-volume-table"
							>
								<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
									<tr>
										<Th>Event name</Th>
										<Th>Category</Th>
										<Th className="text-right">Volume</Th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-200">
									{props.stats.top_10_by_volume.map((row) => (
										<tr
											key={row.id}
											data-testid={`top-row-${row.id}`}
											className="hover:bg-zinc-50/80"
										>
											<Td>
												<code className="font-mono text-zinc-900">
													{row.name}
												</code>
											</Td>
											<Td>
												<CategoryBadge category={row.category} />
											</Td>
											<Td className="text-right text-zinc-900">
												{formatVolume(row.volume_lifetime)}
											</Td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : null}
				</>
			)}
		</section>
	);
}

function StatTile(props: { label: string; value: string }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
			<p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
				{props.label}
			</p>
			<p className="mt-1 text-base font-semibold text-zinc-900">
				{props.value}
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Registry card
// ---------------------------------------------------------------------------

function RegistryCard(props: {
	rows: readonly SdkEventType[];
	totalRows: number;
	loading: boolean;
	error: string | null;
	categoryFilter: EventCategory | null;
	onCategoryFilter: (c: EventCategory | null) => void;
	search: string;
	onSearch: (s: string) => void;
	onEdit: (args: {
		id: string;
		category?: EventCategory;
		display_name?: string;
		default_payout_cents?: number;
	}) => void;
	onDelete: (id: string) => void;
	onAdd: () => void;
	updating: boolean;
	deleting: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col`} aria-label="Event types">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5">
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Registered events
					</h2>
					<p className={HINT}>
						{props.totalRows === 0
							? "No events registered yet."
							: `${props.totalRows} event type${props.totalRows === 1 ? "" : "s"} registered.`}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<CategoryChip
						active={props.categoryFilter === null}
						onClick={() => props.onCategoryFilter(null)}
					>
						All
					</CategoryChip>
					{EVENT_CATEGORIES.map((c) => (
						<CategoryChip
							key={c}
							active={props.categoryFilter === c}
							onClick={() =>
								props.onCategoryFilter(
									props.categoryFilter === c ? null : c,
								)
							}
						>
							{categoryLabel(c)}
						</CategoryChip>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<input
						type="text"
						aria-label="Search events"
						placeholder="Search by name or label…"
						className={`${INPUT} max-w-sm`}
						value={props.search}
						onChange={(e) => props.onSearch(e.currentTarget.value)}
						data-testid="registry-search"
					/>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onAdd}
						data-testid="registry-add"
					>
						Add event type
					</button>
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading events.</div>
			) : props.rows.length === 0 ? (
				<div className="flex flex-col items-center gap-1 p-10 text-center">
					<p className="text-sm font-medium text-zinc-700">
						No matching events.
					</p>
					<p className="text-xs text-zinc-500">
						{props.totalRows === 0
							? "Register your first event type to start tracking."
							: "Clear filters to see all registered events."}
					</p>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left text-sm"
						data-testid="registry-table"
					>
						<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Name</Th>
								<Th>Label</Th>
								<Th>Category</Th>
								<Th className="text-right">Default payout</Th>
								<Th className="text-right">Volume</Th>
								<Th>Created</Th>
								<Th className="text-right">Actions</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.rows.map((row) => (
								<RegistryRow
									key={row.id}
									row={row}
									onEdit={props.onEdit}
									onDelete={props.onDelete}
									updating={props.updating}
									deleting={props.deleting}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function RegistryRow(props: {
	row: SdkEventType;
	onEdit: (args: {
		id: string;
		category?: EventCategory;
		display_name?: string;
		default_payout_cents?: number;
	}) => void;
	onDelete: (id: string) => void;
	updating: boolean;
	deleting: boolean;
}) {
	const [editing, setEditing] = useState(false);
	const [displayName, setDisplayName] = useState(props.row.display_name);
	const [category, setCategory] = useState<EventCategory>(props.row.category);
	const [payout, setPayout] = useState(String(props.row.default_payout_cents));
	const [errors, setErrors] = useState<{
		display_name?: string;
		default_payout_cents?: string;
	}>({});

	const reset = () => {
		setDisplayName(props.row.display_name);
		setCategory(props.row.category);
		setPayout(String(props.row.default_payout_cents));
		setErrors({});
		setEditing(false);
	};

	const save = () => {
		const next: typeof errors = {};
		const validDn = validateDisplayName(displayName);
		if (validDn === "invalid" || validDn === null)
			next.display_name = "Keep it 1-120 chars.";
		const validPay = validatePayoutCents(payout);
		if (validPay === "invalid")
			next.default_payout_cents = "Non-negative integer cents.";
		setErrors(next);
		if (Object.keys(next).length > 0) return;
		props.onEdit({
			id: props.row.id,
			display_name: validDn as string,
			category,
			default_payout_cents: validPay === null ? 0 : (validPay as number),
		});
		setEditing(false);
	};

	if (editing) {
		return (
			<tr
				data-testid={`event-row-${props.row.id}`}
				className="hover:bg-zinc-50/80"
			>
				<Td>
					<code className="font-mono text-zinc-900">{props.row.name}</code>
					{props.row.volume_lifetime > 0 ? (
						<p className="mt-1 text-xs text-zinc-500">Locked — already used</p>
					) : null}
				</Td>
				<Td colSpan={2}>
					<input
						type="text"
						maxLength={120}
						className={INPUT}
						value={displayName}
						onChange={(e) => setDisplayName(e.currentTarget.value)}
						data-testid={`edit-display-name-${props.row.id}`}
					/>
					{errors.display_name ? (
						<p className="mt-1 text-xs text-red-600" role="alert">
							{errors.display_name}
						</p>
					) : null}
					<select
						className={`${INPUT} mt-2`}
						value={category}
						onChange={(e) =>
							setCategory(e.currentTarget.value as EventCategory)
						}
						data-testid={`edit-category-${props.row.id}`}
					>
						{EVENT_CATEGORIES.map((c) => (
							<option key={c} value={c}>
								{categoryLabel(c)}
							</option>
						))}
					</select>
				</Td>
				<Td className="text-right">
					<input
						type="number"
						min={0}
						step={1}
						className={`${INPUT} text-right`}
						value={payout}
						onChange={(e) => setPayout(e.currentTarget.value)}
						data-testid={`edit-payout-${props.row.id}`}
					/>
					{errors.default_payout_cents ? (
						<p className="mt-1 text-xs text-red-600" role="alert">
							{errors.default_payout_cents}
						</p>
					) : null}
				</Td>
				<Td className="text-right text-zinc-900">
					{formatVolume(props.row.volume_lifetime)}
				</Td>
				<Td className="text-zinc-600">{formatDate(props.row.created_at)}</Td>
				<Td className="text-right">
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={save}
							disabled={props.updating}
							data-testid={`save-${props.row.id}`}
						>
							Save
						</button>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={reset}
							disabled={props.updating}
						>
							Cancel
						</button>
					</div>
				</Td>
			</tr>
		);
	}

	return (
		<tr
			data-testid={`event-row-${props.row.id}`}
			className="hover:bg-zinc-50/80"
		>
			<Td>
				<code className="font-mono text-zinc-900">{props.row.name}</code>
				{!props.row.active ? (
					<p className="mt-1 text-xs text-zinc-500">Deleted</p>
				) : null}
			</Td>
			<Td>
				<span className="text-zinc-900">{props.row.display_name}</span>
			</Td>
			<Td>
				<CategoryBadge category={props.row.category} />
			</Td>
			<Td className="text-right text-zinc-900">
				{formatCents(props.row.default_payout_cents)}
			</Td>
			<Td className="text-right text-zinc-900">
				{formatVolume(props.row.volume_lifetime)}
			</Td>
			<Td className="text-zinc-600">{formatDate(props.row.created_at)}</Td>
			<Td className="text-right">
				<div className="flex justify-end gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={() => setEditing(true)}
						disabled={!props.row.active || props.updating}
						data-testid={`edit-${props.row.id}`}
					>
						Edit
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={() => props.onDelete(props.row.id)}
						disabled={!props.row.active || props.deleting}
						data-testid={`delete-${props.row.id}`}
					>
						Delete
					</button>
				</div>
			</Td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Register modal
// ---------------------------------------------------------------------------

function RegisterModal(props: {
	onClose: () => void;
	onSubmit: (args: {
		name: string;
		category: EventCategory;
		display_name: string;
		default_payout_cents: number;
	}) => void;
	busy: boolean;
}) {
	const [name, setName] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [category, setCategory] = useState<EventCategory>("custom");
	const [payout, setPayout] = useState("0");
	const [errors, setErrors] = useState<{
		name?: string;
		display_name?: string;
		category?: string;
		default_payout_cents?: string;
	}>({});

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next: typeof errors = {};
		const validName = validateName(name);
		if (!validName)
			next.name =
				"Use snake_case, 2-50 chars. Must start with a lowercase letter.";
		const validDn = validateDisplayName(displayName);
		if (validDn === "invalid" || validDn === null)
			next.display_name = "Display label is required (1-120 chars).";
		const validCat = validateCategory(category);
		if (!validCat) next.category = "Pick a valid category.";
		const validPay = validatePayoutCents(payout);
		if (validPay === "invalid")
			next.default_payout_cents = "Non-negative integer cents.";

		setErrors(next);
		if (Object.keys(next).length > 0) return;

		props.onSubmit({
			name: validName!,
			display_name: validDn as string,
			category: validCat!,
			default_payout_cents: validPay === null ? 0 : (validPay as number),
		});
	};

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Register event type"
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/30 p-4"
			data-testid="register-modal"
		>
			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex w-full max-w-lg flex-col gap-4 p-6`}
				data-testid="register-form"
			>
				<h2 className="text-lg font-semibold text-zinc-900">
					Register event type
				</h2>
				<p className={HINT}>
					The event name is permanent once the event is used. You can change the
					display label, category, or default payout later.
				</p>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="se-name" className={LABEL}>
						Event name
					</label>
					<input
						id="se-name"
						name="name"
						required
						maxLength={50}
						className={INPUT}
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						placeholder="level_up"
						disabled={props.busy}
						data-testid="register-name"
					/>
					{errors.name ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.name}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="se-display" className={LABEL}>
						Display label
					</label>
					<input
						id="se-display"
						name="display_name"
						required
						maxLength={120}
						className={INPUT}
						value={displayName}
						onChange={(e) => setDisplayName(e.currentTarget.value)}
						placeholder="Level up"
						disabled={props.busy}
						data-testid="register-display-name"
					/>
					{errors.display_name ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.display_name}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="se-category" className={LABEL}>
						Category
					</label>
					<select
						id="se-category"
						name="category"
						className={INPUT}
						value={category}
						onChange={(e) =>
							setCategory(e.currentTarget.value as EventCategory)
						}
						disabled={props.busy}
						data-testid="register-category"
					>
						{EVENT_CATEGORIES.map((c) => (
							<option key={c} value={c}>
								{categoryLabel(c)}
							</option>
						))}
					</select>
					{errors.category ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.category}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="se-payout" className={LABEL}>
						Default payout (cents)
					</label>
					<input
						id="se-payout"
						name="default_payout_cents"
						type="number"
						min={0}
						step={1}
						className={INPUT}
						value={payout}
						onChange={(e) => setPayout(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="register-payout"
					/>
					{errors.default_payout_cents ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.default_payout_cents}
						</p>
					) : null}
				</div>

				<div className="mt-2 flex justify-end gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onClose}
						disabled={props.busy}
						data-testid="register-cancel"
					>
						Cancel
					</button>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={props.busy}
						data-testid="register-submit"
					>
						{props.busy ? "Registering." : "Register event"}
					</button>
				</div>
			</form>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function CategoryChip(props: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			className={`rounded-[6px] border px-3 py-1 text-xs font-medium transition ${
				props.active
					? "border-emerald-500 bg-emerald-50 text-emerald-700"
					: "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
			}`}
			data-testid={`category-chip-${typeof props.children === "string" ? props.children.toLowerCase() : "all"}`}
		>
			{props.children}
		</button>
	);
}

function CategoryBadge(props: { category: EventCategory }) {
	const palette: Record<EventCategory, string> = {
		engagement: "border-emerald-200 bg-emerald-50 text-emerald-700",
		monetization: "border-sky-200 bg-sky-50 text-sky-700",
		social: "border-violet-200 bg-violet-50 text-violet-700",
		game: "border-amber-200 bg-amber-50 text-amber-700",
		onboarding: "border-cyan-200 bg-cyan-50 text-cyan-700",
		custom: "border-zinc-200 bg-zinc-50 text-zinc-700",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${palette[props.category]}`}
		>
			{categoryLabel(props.category)}
		</span>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th scope="col" className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}>
			{props.children}
		</th>
	);
}

function Td(props: {
	children: React.ReactNode;
	className?: string;
	colSpan?: number;
}) {
	return (
		<td
			colSpan={props.colSpan}
			className={`px-4 py-3 align-top text-zinc-700 ${props.className ?? ""}`.trim()}
		>
			{props.children}
		</td>
	);
}

export { SdkCustomEventsPage as SdkCustomEventsPageNamed };
